import React, { useEffect, useState, useRef, useMemo } from 'react';
import { sortFiltersBySitecore } from '../../../helpers';
import { useTab } from './tab';
import { addFiltersPerFacetValue } from '../../../helpers/coveoFacet';

export const Facet = props => {
  const {
    controller,
    labels,
    valueNameOverride,
    sortedFacets,
    field,
    numberOfValues = 999,
    filtersPerFacetValue,
    headlessEngine,
    defaultTab
  } = props;
  const [state, setState] = useState(controller.state);
  const [expanded, setExpanded] = useState(props.index === 0 ? true : false);
  const [hideCount, setHideCount] = useState(true);
  const [firstLoad, setFirstLoad] = useState(true);
  const facetList = useRef(null);
  const [facetListHeight, setFacetListHeight] = useState(0);
  const [sortedValues, setSortedValues] = useState([]);
  const [showMore, setShowMore] = useState(null);

  let filterValue = null,
    tabPerFacet = null;

  if (filtersPerFacetValue && filtersPerFacetValue?.length > 0) {
    const filterFacetObject = filtersPerFacetValue.find(
      facet => facet.facetField === field.value
    );

    if (filterFacetObject) {
      const { facetValue, context, tab, mlParameters } = filterFacetObject;
      filterValue = facetValue;

      if (mlParameters || context) {
        addFiltersPerFacetValue(field, facetValue, context, mlParameters);
      }

      if (tab) {
        tabPerFacet = useMemo(() => useTab(headlessEngine, tab), [tab]);
      }
    }
  }

  const handleCollapseEvent = () => {
    setExpanded(false);
  };

  const handleShowMore = () => {
    setShowMore(prev => !prev);
  };

  const getNameOverride = name => {
    const override = valueNameOverride.filter(obj => {
      return obj.value === name;
    });
    return override.length ? override[0].label : name;
  };

  useEffect(() => {
    controller.subscribe(() => setState(controller.state));
  }, [controller]);

  useEffect(() => {
    const selected = state.values.find(value => value.state === 'selected');

    if (state?.values?.length > 0) {
      const sortedFilters = sortFiltersBySitecore(state.values, sortedFacets);

      setSortedValues(sortedFilters);

      if (firstLoad) {
        setFirstLoad(false);
        if (state.values.length > numberOfValues) {
          setShowMore(true);
        }
        if (selected) {
          setExpanded(true);

          if (
            sortedFilters.findIndex(filter => filter.value === selected.value) >
            numberOfValues
          ) {
            setShowMore(false);
          }
        }
      }
    }

    if (!firstLoad) {
      if (state.values.length <= numberOfValues) {
        setShowMore(null);
      } else {
        setShowMore(true);
      }
    }
  }, [state.values.length, expanded]);

  useEffect(() => {
    if (sortedValues.length > 0) {
      if (facetList.current && facetListHeight !== facetList.current.height) {
        resizeFacetHeight();
      }
    }
  }, [sortedValues, showMore]);

  const resizeFacetHeight = () => {
    setFacetListHeight(facetList.current.offsetHeight);
  };

  useEffect(() => {
    window.addEventListener('coveoFacet.collapseAll', handleCollapseEvent);
    window.addEventListener('resize', resizeFacetHeight);

    window.addEventListener('clickontab', resizeFacetHeight);
    return () => {
      window.removeEventListener('coveoFacet.collapseAll');
      window.removeEventListener('resize', resizeFacetHeight);

      window.removeEventListener('clickontab', resizeFacetHeight);
    };
  }, []);

  const handleFacetClick = value => {
    if (filterValue) {
      const lowerCaseValue = value.value?.toLowerCase();
      const lowerCasefilterValue = filterValue?.toLowerCase();

      const selectedValues = controller.state.values.filter(
        val => val.state === 'selected'
      );

      const currentValue = selectedValues.find(
        val => val.value?.toLowerCase() === lowerCaseValue
      );

      const isfilterValueSelected = selectedValues.find(
        val => val.value?.toLowerCase() === lowerCasefilterValue
      );

      if (
        (lowerCasefilterValue === lowerCaseValue &&
          !isfilterValueSelected &&
          selectedValues.length < 1) ||
        (lowerCasefilterValue !== lowerCaseValue &&
          isfilterValueSelected &&
          currentValue &&
          controller.isValueSelected(currentValue) &&
          selectedValues.length - 1 <= 1)
      ) {
        if (tabPerFacet) {
          tabPerFacet.select();
        }
      } else {
        if (tabPerFacet && defaultTab) {
          defaultTab.select();
        }
      }
    }

    controller.toggleSelect(
      state?.values.find(val => val.value === value.value)
    );
  };

  if (!state.values.length) {
    return;
  }

  return (
    <div className="coveo-search-results__facet accordion-item">
      <h3
        aria-label={props.title}
        className="coveo-search-results__facet-heading accordion-item__title"
      >
        <button
          aria-expanded={expanded}
          aria-controls={`coveo-facet-${props.title}`}
          aria-label={`${props.title}`}
          className="coveo-search-results__facet-toggle accordion-item__trigger"
          onClick={() => {
            setExpanded(!expanded);
          }}
        >
          {expanded === true ? (
            <svg
              className="accordion-item__trigger-icon accordion-item__trigger-icon--collapse"
              height="16"
              role="presentation"
              viewBox="0 0 120 120"
              width="16"
            >
              <use xlinkHref="#minus-regular"></use>
            </svg>
          ) : (
            <svg
              className="accordion-item__trigger-icon accordion-item__trigger-icon--expand"
              height="16"
              role="presentation"
              viewBox="0 0 120 120"
              width="16"
            >
              <use xlinkHref="#plus-regular"></use>
            </svg>
          )}
          <span className="accordion-item__trigger-text">{props.title}</span>
        </button>
      </h3>
      <div
        id={`coveo-facet-${props.title}`}
        className="coveo-search-results__facet-value-list accordion-item__panel"
        data-expanded={expanded}
        style={{
          '--coveo-facet-value-list-height': `${facetListHeight}px`
        }}
      >
        <div ref={facetList} className="accordion-item__panel-inner">
          <div className="accordion-item__copy">
            <ul className="system-list__filter-list">
              {sortedValues.map((value, index) => (
                <React.Fragment key={value.value}>
                  {index >= numberOfValues && showMore ? null : (
                    <>
                      {state?.values.find(val => val.value === value.value) ? (
                        <li className="system-list__filter-item ">
                          <div className="form-checkbox__wrapper form-checkbox--small">
                            <label
                              key={value.value}
                              htmlFor={value.value}
                              className="coveo-search-results__facet-value form-checkbox"
                            >
                              <input
                                className="coveo-search-results__facet-value-input form-checkbox__input"
                                name={value.value}
                                id={value.value}
                                type="checkbox"
                                checked={controller.isValueSelected(
                                  state?.values.find(
                                    val => val.value === value.value
                                  )
                                )}
                                onChange={() => handleFacetClick(value)}
                                disabled={state.isLoading}
                              />
                              <div
                                className="checkmark__wrapper"
                                role="presentation"
                              >
                                <svg
                                  className="checkmark__svg"
                                  viewBox="0 0 120 120"
                                >
                                  <use xlinkHref="#checkmark-regular"></use>
                                </svg>
                              </div>
                              {valueNameOverride ? (
                                <span className="label">
                                  {getNameOverride(value.value)}
                                </span>
                              ) : (
                                <span className="label">{value.value}</span>
                              )}
                            </label>
                          </div>
                          {!hideCount && (
                            <span className="system-list__filter-count">
                              ({value.numberOfResults})
                            </span>
                          )}
                        </li>
                      ) : null}
                    </>
                  )}
                </React.Fragment>
              ))}
            </ul>
            {showMore !== null && (
              <div className="coveo-facet__show-buttons">
                {showMore && (
                  <button
                    className="coveo-facet__show-button"
                    onClick={handleShowMore}
                  >
                    {labels.showMore}
                  </button>
                )}
                {!showMore && (
                  <button
                    className="coveo-facet__show-button"
                    onClick={handleShowMore}
                  >
                    {labels.showLess}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
